import { useCallback, useId, useMemo, useRef } from 'react';
import { FollowSelector } from '../components/FollowSelector';
import { useFollowSelectorContext } from '../context/FollowSelectorContext';
import { useGuestSave } from '~/modules/moodboards/hooks/use-guest-save';
import { resolveFixedOffset } from '../utils/fixed-containing-block';
import { isUserLoggedIn, useUser } from '@modules/user';
import { resolveTrackedPinId } from '~/modules/moodboards/state/design-board-save-state';
import type { PinItemType } from '~/modules/moodboards/type';

interface UseFollowSelectorProxyArgs {
    pinId: number;
    pinType: PinItemType;
    libraryItemId?: number;
    /** Item name for the save gate's success screen (guest saves). */
    title?: string;
    /** Item image for the save gate's brand pane (guest saves). */
    image?: string;
    onFollowChange?: (isFollowed: boolean) => void;
}

/**
 * Provides a programmatic way to open the FollowSelector while preserving existing button UI.
 * Useful when the trigger is rendered internally by Aria components and cannot be wrapped.
 *
 * Opens the selector by calling the context directly (no hidden button proxy).
 * Positions the popover at the clicked element by imperatively setting the anchor's
 * fixed position to match the target's bounding rect.
 *
 * Corrects for ancestors that capture fixed descendants (carousel transforms,
 * contained tile media) and would otherwise displace the popover.
 *
 * Pass a React.MouseEvent to `openFollowSelector(e)` for accurate positioning.
 */
export const useFollowSelectorProxy = ({
    pinId,
    pinType,
    libraryItemId,
    title,
    image,
    onFollowChange,
}: UseFollowSelectorProxyArgs) => {
    const instanceId = useId();
    const user = useUser();
    const isLoggedIn = isUserLoggedIn(user);
    const { openFollowSelector: contextOpen, openSelectorId } =
        useFollowSelectorContext();
    const { guestSave } = useGuestSave();
    const anchorRef = useRef<HTMLSpanElement>(null);

    // The same pin identity the board picker and the save-state atoms use:
    // the image id for a PhotoPin, the page id otherwise. Keyed the other way
    // round the picker reads some unrelated saved page as this one.
    const selectedItemIds = useMemo(
        () => [resolveTrackedPinId(pinId, libraryItemId, pinType)],
        [libraryItemId, pinId, pinType],
    );

    /**
     * Position the anchor span at the target element's viewport location,
     * correcting for any ancestor that captures fixed descendants.
     */
    const positionAnchorAt = useCallback((element: HTMLElement) => {
        const anchor = anchorRef.current;
        if (!anchor) return;
        const targetRect = element.getBoundingClientRect();
        const { top, left } = resolveFixedOffset(anchor, targetRect);

        anchor.style.top = `${top}px`;
        anchor.style.left = `${left}px`;
        anchor.style.width = `${targetRect.width}px`;
        anchor.style.height = `${targetRect.height}px`;
    }, []);

    /**
     * Resolve the best DOM element to anchor the popover to.
     * Prefers the closest <button> ancestor of the actual click target (the heart icon),
     * falling back to currentTarget or the target itself.
     */
    const resolveAnchorTarget = useCallback(
        (
            eventOrElement: React.SyntheticEvent<HTMLElement> | HTMLElement,
        ): HTMLElement | null => {
            if (eventOrElement instanceof HTMLElement) {
                return eventOrElement;
            }

            // Use the actual clicked element and walk up to the nearest button
            // for accurate positioning (e.currentTarget can be a large wrapper).
            const clicked = eventOrElement.target as HTMLElement | null;
            return (
                clicked?.closest<HTMLElement>('button') ??
                (eventOrElement.currentTarget as HTMLElement | null) ??
                clicked
            );
        },
        [],
    );

    /**
     * Open the follow selector popover.
     * Pass a MouseEvent or HTMLElement for accurate positioning next to the trigger.
     * For logged-out users, shows the auth modal so they can log in first.
     */
    const openFollowSelector = useCallback(
        (eventOrElement?: React.SyntheticEvent<HTMLElement> | HTMLElement) => {
            // Guest flow: store pin and show auth modal
            if (!isLoggedIn) {
                guestSave({ pinId, pinType, libraryItemId, title, image });
                return;
            }

            // Always open the selector first — positioning is best-effort
            contextOpen(instanceId, {
                selectedItemIds,
                selectedItemType: pinType,
            });

            if (eventOrElement) {
                const target = resolveAnchorTarget(eventOrElement);
                if (target) {
                    positionAnchorAt(target);
                }
            }
        },
        [
            isLoggedIn,
            guestSave,
            pinId,
            pinType,
            libraryItemId,
            title,
            image,
            contextOpen,
            instanceId,
            selectedItemIds,
            positionAnchorAt,
            resolveAnchorTarget,
        ],
    );

    const followSelector = useMemo(
        () => (
            <FollowSelector
                instanceId={instanceId}
                anchorRef={anchorRef}
                pinId={pinId}
                libraryItemId={libraryItemId}
                pinType={pinType}
                onFollowChange={onFollowChange}
            />
        ),
        [instanceId, libraryItemId, onFollowChange, pinId, pinType],
    );

    /** Whether this instance's board-picker popover is currently open. */
    const isOpen = openSelectorId === instanceId;

    return { followSelector, openFollowSelector, isOpen };
};
